import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import { UpdateUserGroup } from "../Services/APIService";

interface GroupData {
  groupId: number;
  groupName: string;
  isActive: boolean;
  tenantId: number;
}

interface UpdateGroupCardProps {
  groupData: GroupData | null;
  onUpdateComplete: () => void;
  onCancel: () => void;
}

interface FormData {
  groupId: number | "";
  groupName: string;
  isActive: boolean;
}

const UpdateGroupCard: React.FC<UpdateGroupCardProps> = ({
  groupData,
  onUpdateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    groupId: "",
    groupName: "",
    isActive: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set form data when groupData changes
  useEffect(() => {
    if (groupData) {
      setFormData({
        groupId: groupData.groupId,
        groupName: groupData.groupName,
        isActive:
          typeof groupData.isActive === "boolean"
            ? groupData.isActive
            : groupData.isActive === "true",
      });
    }
  }, [groupData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "groupId"
          ? value === ""
            ? ""
            : parseInt(value)
          : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (formData.groupId === "" || isNaN(Number(formData.groupId))) {
      setError("Please enter a valid Group ID");
      return;
    }

    if (!formData.groupName.trim()) {
      setError("Please enter a Group Name");
      return;
    }

    try {
      setIsLoading(true);

      // Get user ID from localStorage
      const userId = localStorage.getItem("UserId");

      if (!userId) {
        throw new Error("User ID not found in localStorage");
      }

      // Prepare request payload
      const payload = {
        groupID: formData.groupId,
        groupName: formData.groupName.trim().replace(/\s+/g, " "),
        isActive: formData.isActive,
        modifiedBy: parseInt(userId),
      };

      // You'll need to create an updateUserGroup function in your APIService
      const data = await UpdateUserGroup(payload);

      if (data.isUpdated) {
        toast.success("Group Updated Successfully!");
        // Notify parent component that update is complete
        onUpdateComplete();
      } else {
        toast.error(data.message || "Group update failed!");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    // Call the cancel function
    onCancel();
  };

  return (
    <div className="card">
      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Update Group</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger text-danger" role="alert">
              {error}
            </div>
          )}
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <input
                type="number"
                name="groupId"
                className="form-control form-control-lg"
                placeholder="Enter Group Id"
                value={formData.groupId}
                onChange={handleChange}
                disabled // Disable editing of Group ID
                required
              />
            </div>
            <div className="col-12 col-lg-6 mb-3">
              <input
                type="text"
                name="groupName"
                className="form-control form-control-lg"
                placeholder="Enter Group Name"
                value={formData.groupName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-12 col-lg-6 mb-3">
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <span className="form-check-label">Active</span>
              </label>
            </div>
            <div className="col-12 mt-2">
              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateGroupCard;
